/**
 * WP11a / #46644 — old product under the HG8 `/classic` compatibility namespace.
 *
 * `/` is the production verification shell (WP11b / #46645). This file does not
 * unmount that shell. Old product code is preserved here, not deleted.
 * Remaining old-product routes (`/work`, …) stay as HG7 compatibility
 * direct-routes (not deleted in this cutover).
 *
 * Visual mark: `data-classic-mark` + Alert "Classic product" so the archive
 * cannot be mistaken for the live verification IA. `/classic` is NOT a
 * bare-canvas path — the old mode rail wraps these pages on purpose.
 *
 * tree.ts registers the literal `/classic` route before `/classic/verification`.
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import { Alert, AlertDescription, AlertTitle } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { evidence } from "@excellent/ui/api/contracts/evidence";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import { RouterLink as Link, useNavigate } from "@excellent/ui/router";
import { VerifierHome } from "../shared/agents/verifier-home";
import { rootRoute } from "./__root";
import { hopLeftoverLegacyRoute } from "./agents-unmount-hop";

function ClassicMark() {
  return (
    <Alert data-classic-mark="true">
      <AlertTitle>Classic product</AlertTitle>
      <AlertDescription>
        Pre-cutover shell. The verification product lives at{" "}
        <Link href="/" className="font-medium text-primary hover:underline">
          /
        </Link>
        .
      </AlertDescription>
    </Alert>
  );
}

export function OldVerifierHomeBody() {
  const navigate = useNavigate();
  const [soc2PreflightOpen, setSoc2PreflightOpen] = React.useState(false);
  const {
    data: listData,
    error: listError,
    isLoading: listLoading,
  } = useApi(evidence.list, { query: { status: "all", limit: 100 } });
  const { data: silent } = useApi(evidence.silentApprove, { query: undefined });
  const { data: readiness } = useApi(evidence.readiness, { query: undefined });
  const { data: universalOverview } = useApi(universalVerification.overview, { query: undefined });
  const { data: soc2Preflight } = useApi(
    evidence.soc2PreflightSample,
    soc2PreflightOpen ? { query: undefined } : null,
  );
  const { data: soc2RunsData, mutate: mutateSoc2Runs } = useApi(
    evidence.soc2PreflightRuns,
    soc2PreflightOpen ? { query: {} } : null,
  );
  const {
    data: soc2ImportOnboardingState,
    mutate: mutateSoc2ImportOnboardingState,
  } = useApi(
    evidence.soc2PreflightImportOnboardingState,
    soc2PreflightOpen ? { query: {} } : null,
  );
  const { data: soc2PolicyStudioState } = useApi(
    evidence.soc2PreflightPolicyStudioState,
    soc2PreflightOpen ? { query: {} } : null,
  );
  const { data: soc2BatchJobsData, mutate: mutateSoc2BatchJobs } = useApi(
    evidence.soc2PreflightBatchJobs,
    soc2PreflightOpen ? { query: undefined } : null,
  );
  const {
    data: workflowCandidatesData,
    mutate: mutateWorkflowCandidates,
  } = useApi(evidence.workflowCandidates, { query: undefined });

  const open = (id: number) => {
    navigate.push(`/runs/${id}`);
  };

  return (
    <VerifierHome
      tasks={listData?.tasks ?? []}
      silent={silent}
      readiness={readiness}
      universalOverview={universalOverview}
      soc2Preflight={soc2Preflight}
      soc2ImportOnboardingState={soc2ImportOnboardingState}
      soc2PolicyStudioState={soc2PolicyStudioState}
      soc2BatchJobs={soc2BatchJobsData?.jobs ?? []}
      soc2Runs={soc2RunsData?.runs ?? []}
      soc2PreflightOpen={soc2PreflightOpen}
      onOpenSoc2Preflight={() => setSoc2PreflightOpen(true)}
      workflowCandidates={workflowCandidatesData?.candidates ?? []}
      listError={listError}
      listLoading={listLoading && !listData}
      onOpen={open}
      onSoc2RunImported={() => {
        void mutateSoc2Runs();
        void mutateSoc2ImportOnboardingState();
        void mutateSoc2BatchJobs();
      }}
      onWorkflowCandidateSaved={() => {
        void mutateWorkflowCandidates();
      }}
    />
  );
}

/** Old WorkspaceHome — VerifierHome entry, now at `/classic`. */
function ClassicWorkspaceHome() {
  return (
    <div
      data-route="classic"
      data-classic-product="workspace-home"
      className="space-y-6 px-6 py-8"
    >
      <ClassicMark />
      <OldVerifierHomeBody />
    </div>
  );
}

/** Old VerifierHome console entry, now at `/classic/verification`. */
function ClassicVerifierHome() {
  return (
    <div
      data-route="classic-verification"
      data-classic-product="verifier-home"
      className="space-y-6 px-6 py-8"
    >
      <ClassicMark />
      <OldVerifierHomeBody />
    </div>
  );
}

export const classicHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/classic",
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
  component: ClassicWorkspaceHome,
});

export const classicVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/classic/verification",
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
  component: ClassicVerifierHome,
});
